import React, { useEffect, useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Calendar, Wallet, Receipt, AlertTriangle } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { helper } from "@/lib/helper";
import { cn } from "@/lib/utils";

type ScheduleRow = {
  id?: number | string;
  installment?: number | string;
  amount?: number | string;
  paid_amount?: number | string;
  balance?: number | string;
  date?: string; // YYYY-MM-DD
};

type PaginationMeta = {
  current_page: number;
  per_page: number;
  total: number;
  last_page: number;
  has_more: boolean;
};

function isOverdue(dateStr?: string, balance?: any) {
  const bal = Number(balance) || 0;
  if (!dateStr || bal <= 0) return false;

  // parse YYYY-MM-DD safely as local date
  const d = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(d.getTime())) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return d.getTime() < today.getTime();
}

export default function ActiveLoanScheduleTab({ customerId }: { customerId: string }) {
  const perPage = 30;

  const [rows, setRows] = useState<ScheduleRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [page, setPage] = useState(1);

  async function fetchSchedule(p = 1) {
    if (!customerId) return;
    if (loading) return;

    setLoading(true);
    setErr("");

    try {
      const res = await apiFetch<any>(
        `/customer-profile/${customerId}/schedule?page=${p}&per_page=${perPage}`
      );

  const payload = res?.data ?? res;

// ✅ if payload is already an array, use it directly
const rows: ScheduleRow[] = Array.isArray(payload)
  ? payload
  : Array.isArray(payload?.data)
    ? payload.data
    : Array.isArray(payload?.data?.data)
      ? payload.data.data
      : [];

const m: PaginationMeta | null =
  (!Array.isArray(payload) && (payload?.meta ?? payload?.data?.meta)) ?? null;

setRows(rows);
setMeta(m);
setPage(p);

    } catch (e: any) {
      setErr(e?.message || "Failed to load schedule");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchSchedule(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const totals = useMemo(() => {
    const totalBalance = rows.reduce((sum, r) => sum + (Number(r.balance) || 0), 0);
    const overdueCount = rows.reduce(
      (sum, r) => sum + (isOverdue(r.date, r.balance) ? 1 : 0),
      0
    );
    const overdueBalance = rows.reduce(
      (sum, r) => sum + (isOverdue(r.date, r.balance) ? (Number(r.balance) || 0) : 0),
      0
    );
    return { totalBalance, overdueCount, overdueBalance };
  }, [rows]);

  const canPrev = page > 1 && !loading;
  const canNext = Boolean(meta?.has_more) && !loading;

  return (
    <div className="space-y-3">
      {/* HEADER */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-sm font-semibold">Active Loan Schedule</div>

        <Button
          variant="outline"
          size="sm"
          onClick={() => fetchSchedule(1)}
          disabled={loading}
          className="gap-2"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          Refresh
        </Button>
      </div>

      {/* OVERDUE SUMMARY (enterprise highlight) */}
      {rows.length ? (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <KpiMini label="Total Balance" value={helper.nf(totals.totalBalance)} />
          <KpiMini
            label="Overdue Installments"
            value={String(totals.overdueCount)}
            danger={totals.overdueCount > 0}
          />
          <KpiMini
            label="Overdue Balance"
            value={helper.nf(totals.overdueBalance)}
            danger={totals.overdueBalance > 0}
          />
        </div>
      ) : null}

      {/* ERROR */}
      {err ? (
        <Card>
          <CardContent className="p-4 text-sm text-destructive">{err}</CardContent>
        </Card>
      ) : null}

      {/* DESKTOP TABLE */}
      <div className="hidden lg:block overflow-x-auto rounded-xl border">
        <table className="w-full text-sm min-w-[820px]">
          <thead className="bg-muted/50">
            <tr className="text-left">
              <th className="p-3">Installment</th>
              <th className="p-3">Date</th>
              <th className="p-3">Amount</th>
              <th className="p-3">Paid</th>
              <th className="p-3">Balance</th>
              <th className="p-3">Status</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="p-6 text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading schedule...
                  </div>
                </td>
              </tr>
            ) : rows.length ? (
              rows.map((r) => {
                const overdue = isOverdue(r.date, r.balance);
                const bal = Number(r.balance) || 0;

                return (
                  <tr
                    key={String(r.id)}
                    className={cn("border-t", overdue ? "bg-red-500/5" : "")}
                  >
                    <td className="p-3 font-medium">#{helper.safe(r.installment)}</td>

                    <td className="p-3">
                      <span className={cn(overdue ? "text-red-700 font-semibold" : "")}>
                        {helper.safe(r.date)}
                      </span>
                    </td>

                    <td className="p-3">{helper.nf(r.amount)}</td>
                    <td className="p-3">{helper.nf(r.paid_amount)}</td>

                    <td className={cn("p-3 font-semibold", overdue ? "text-red-700" : "text-primary")}>
                      {helper.nf(r.balance)}
                    </td>

                    <td className="p-3">
                      {bal <= 0 ? (
                        <span className="text-xs font-semibold px-2 py-1 rounded-full border bg-emerald-500/10 text-emerald-700 border-emerald-200">
                          Paid
                        </span>
                      ) : overdue ? (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full border bg-red-500/10 text-red-700 border-red-200">
                          <AlertTriangle className="h-3.5 w-3.5" />
                          Overdue
                        </span>
                      ) : (
                        <span className="text-xs font-semibold px-2 py-1 rounded-full border bg-amber-500/10 text-amber-700 border-amber-200">
                          Pending
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={6} className="p-6 text-muted-foreground">
                  No schedule found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* MOBILE CARDS */}
      <div className="lg:hidden space-y-3">
        {loading ? (
          <Card>
            <CardContent className="p-4 text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading schedule...
            </CardContent>
          </Card>
        ) : rows.length ? (
          rows.map((r) => {
            const overdue = isOverdue(r.date, r.balance);
            const bal = Number(r.balance) || 0;

            return (
              <Card
                key={String(r.id)}
                className={cn("shadow-card", overdue ? "border-red-200 bg-red-500/5" : "")}
              >
                <CardContent className="p-3 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-semibold">
                        Installment #{helper.safe(r.installment)}
                      </div>

                      <div className="text-xs text-muted-foreground flex items-center gap-2">
                        <Calendar className={cn("h-3.5 w-3.5", overdue ? "text-red-600" : "text-primary")} />
                        <span className={cn(overdue ? "text-red-700 font-semibold" : "text-foreground")}>
                          {helper.safe(r.date)}
                        </span>
                      </div>
                    </div>

                    {bal <= 0 ? (
                      <span className="text-[11px] font-semibold px-2 py-1 rounded-full border bg-emerald-500/10 text-emerald-700 border-emerald-200">
                        Paid
                      </span>
                    ) : overdue ? (
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-full border bg-red-500/10 text-red-700 border-red-200">
                        <AlertTriangle className="h-3.5 w-3.5" />
                        Overdue
                      </span>
                    ) : (
                      <span className="text-[11px] font-semibold px-2 py-1 rounded-full border bg-amber-500/10 text-amber-700 border-amber-200">
                        Pending
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <Metric icon={Receipt} label="Amount" value={helper.nf(r.amount)} />
                    <Metric icon={Wallet} label="Paid" value={helper.nf(r.paid_amount)} />
                  </div>

                  <div
                    className={cn(
                      "rounded-xl border p-3 flex items-center justify-between",
                      overdue ? "border-red-200 bg-red-500/5" : "bg-muted/20"
                    )}
                  >
                    <div className="text-xs text-muted-foreground font-medium">Balance</div>
                    <div className={cn("text-sm font-bold", overdue ? "text-red-700" : "text-primary")}>
                      {helper.nf(r.balance)}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        ) : (
          <div className="text-sm text-muted-foreground">No schedule found.</div>
        )}
      </div>

      {/* FOOTER / PAGINATION */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-xs text-muted-foreground">
          {meta ? `Page ${meta.current_page} of ${meta.last_page} • Total ${meta.total}` : ""}
        </div>

        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" disabled={!canPrev} onClick={() => fetchSchedule(page - 1)}>
            Prev
          </Button>

          <Button size="sm" variant="outline" disabled={!canNext} onClick={() => fetchSchedule(page + 1)}>
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}

/** small metric tile */
function Metric({
  icon: Icon,
  label,
  value,
}: {
  icon: any;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-xl border bg-muted/20 p-2">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-primary" />
        <div>
          <div className="text-[11px] text-muted-foreground">{label}</div>
          <div className="text-sm font-semibold">{value}</div>
        </div>
      </div>
    </div>
  );
}

function KpiMini({
  label,
  value,
  danger,
}: {
  label: string;
  value: string;
  danger?: boolean;
}) {
  return (
    <Card className="shadow-card">
      <CardContent className="p-3 flex items-center justify-between">
        <div className="text-xs text-muted-foreground font-medium">{label}</div>
        <div className={cn("text-sm font-bold", danger ? "text-red-700" : "text-primary")}>
          {value}
        </div>
      </CardContent>
    </Card>
  );
}